import './styles/app.css';
import './styles/workspace.css';
import './styles/overview.css';
import './styles/clientHub.css';
import './styles/wikiWorkroom.css';
import './styles/deepPages.css';
// CRM shell + client-book design language (Package 1). Imported LAST so its
// `.rail`/`.topbar` rules win the cascade over the old app.css chrome rules
// that share those class names.
import './styles/crm.css';
// Intelligence Plan screen (W1-C) — deltas on top of crm.css's Strategy grammar.
import './styles/plan.css';

import { html, render } from 'lit';
import { rawUrl } from './api/client.ts';
import { AUTH } from './lib/auth.ts';
import { beginLoad, isLoadCurrent, loadPayload, payloadStore } from './state/payload.ts';
import { closeDrawer, initDrawerGlobalListeners } from './ui/drawer.ts';
import { initNotifBell } from './ws/notifBell.ts';
import { openTaskDeepLink } from './ws/taskLink.ts';
import { WS } from './state/workspace.ts';
import { identityMenu } from './ws/board.ts';
import { initPopoverGlobalListeners } from './ui/popover.ts';
import { installFaviconFallback } from './lib/faviconFallback.ts';
import { resolveLanding, shouldApplyLanding } from './lib/personas.ts';
import { applyRoute, currentRoute, initRouter, setClientBrands, visibleSections, type Route } from './lib/router.ts';
import { brandVisual, personInitials } from './lib/identity.ts';
import {
  brandLogo,
  initRegister,
  onClientsLoaded,
  type RegisterClient,
} from './views/register.ts';
import { initCommandPalette } from './ui/commandPalette.ts';
import { initFeedbackPin } from './ui/feedbackPin.ts';
import { renderSearch } from './views/search.ts';
import { renderAI } from './views/ai.ts';
import { initCockpit, teardownCockpit } from './views/cockpit/index.ts';
import { initWork } from './views/work.ts';
import { initTeam } from './views/team.ts';
import { renderProspects } from './views/prospects.ts';

/* Window shim — every global an inline onclick="..." attribute in ported
 * HTML (hub/index.html) or a ported innerHTML template needs to find on
 * `window`. Checklist (extend as more code ports; a missing entry must
 * NOT throw at boot — it just leaves that one control inert):
 *
 * F3c END-STATE SWEEP: every view/ws/ui module in hub/src is now
 * lit-converted. Re-grepped hub/index.html and hub/portfolio.html directly
 * (not just *.ts) for `onclick=`/`onchange=` — index.html has exactly ONE
 * (the drawer close button, `closeDrawer`, below); portfolio.html has
 * zero. `closeDrawer` is therefore the ONLY entry this shim needs, and
 * (per its own note below) the only one that CAN exist here — every other
 * static-HTML-embedded onclick has been retired along with the template
 * that carried it. (Separately, state/workspace.ts also exposes a global —
 * `globalThis.WS = WS` — but that's a distinct, pre-existing F2d pattern
 * for the bare `WS` identifier lit templates reference directly
 * (`${WS.ICON.down}`, `WS.available`, …), not a shim for static-HTML
 * onclick strings; out of scope for this checklist.)
 *   [x] closeDrawer — hub/index.html's drawer close button
 *       (`<button class="close" onclick="closeDrawer()">`). This one
 *       CANNOT be removed even after every view/ui module is lit-
 *       converted — the close button lives in the static index.html
 *       shell, not in any TS-rendered template (checked: ui/drawer.ts's
 *       own openDrawer() never renders a close button of its own).
 *   [removed F3b] openDrawer (id-only) — the §1 result-popover's "Full
 *       asset record" button (ui/popover.ts, old ~1887: `onclick=
 *       "closeResultPopover(); openDrawer('...')"`) was this shim entry's
 *       ONLY consumer (grepped repo-wide); ui/popover.ts's F3 lit-html
 *       conversion imports openDrawer directly (it already has `state` as
 *       a parameter) and calls it via `@click`, so the shim entry is dead
 *       — removed rather than left inert.
 *   [removed F3b] closeResultPopover — the popover's own close button and
 *       the same "Full asset record" button (ui/popover.ts, old ~1875,
 *       ~1887) were this shim entry's ONLY consumers (grepped repo-wide);
 *       both now call closeResultPopover() directly (same module, no
 *       import needed) via `@click` — removed rather than left inert.
 *   [removed F3a] exportQuickStatements, exportSameAs — the §2 Entity
 *       export buttons (views/entity.ts, old ~2334, ~2398, ~2408) were the
 *       ONLY templates referencing either global as a literal onclick=""
 *       string (grepped repo-wide); views/entity.ts's F3 lit-html
 *       conversion replaced both with `@click=${() => fn(state)}` calling
 *       ui/exports.ts's functions directly, so this shim entry is dead —
 *       removed rather than left as an inert no-op.
 *   [removed F3b] setViewMode — the §4 Matrix/Map view toggle buttons
 *       (views/profiles.ts, old ~2642: `onclick="setViewMode('matrix')"` /
 *       `onclick="setViewMode('map')"`) were the ONLY templates
 *       referencing this global as a literal onclick="" string (grepped
 *       repo-wide); views/profiles.ts's F3 lit-html conversion replaced
 *       both with `@click=${() => setViewMode(mode, state)}` calling the
 *       module's own exported function directly (it already took `state`
 *       as its real second parameter — the shim only existed to supply
 *       that from payloadStore.get() for the string-onclick call site),
 *       so this shim entry is dead — removed rather than left inert.
 *   [removed F3c] renderActions — the §7 board toolbar's "clear portfolio
 *       filter" chip and the "my tasks"/"agreed program only" checkboxes
 *       (ws/board.ts, old ~3963/~3965/~3967:
 *       `onclick="WS._urlFilter=null;renderActions()"`,
 *       `onchange="WS._mineOnly=this.checked;renderActions()"`) were this
 *       shim entry's ONLY consumers (grepped repo-wide); ws/board.ts's F3
 *       lit-html conversion replaced all three with `@click`/`@change`
 *       calling `renderActions(payloadStore.get())` directly (imported),
 *       so this shim entry is dead — removed rather than left inert.
 *   [removed F3c] exportActionsCsv — the §7 "Export CSV" button, both the
 *       live-board toolbar (ws/board.ts) and the offline derived-table
 *       header (views/actions.ts), old ~3958/~3097:
 *       `onclick="exportActionsCsv()"`, were this shim entry's ONLY
 *       consumers (grepped repo-wide); both now call
 *       `exportActionsCsv(payloadStore.get())` directly (imported) via
 *       `@click` — removed rather than left inert.
 */
Object.assign(window, {
  closeDrawer: () => closeDrawer(payloadStore.get()),
});

initDrawerGlobalListeners(() => payloadStore.get());
initPopoverGlobalListeners();
installFaviconFallback();

/* CRM shell wiring (Package 1). The rail badge + pinned clients are populated
 * from the SAME GET /clients the register fetches (views/register.ts owns the
 * one fetch; onClientsLoaded fans it out here — no second request). The topbar
 * search trigger lands on the register and focuses its filter (no global-search
 * backend exists — this is the honest behavior). The rail-foot identity button
 * opens the shared account menu (Sign out). */
onClientsLoaded(populateRail);
onClientsLoaded((rows) => setClientBrands(rows));
// Global search (W3-H): the topbar trigger + `/` shortcut open the command
// palette (searches clients · your tasks · screens). Replaces the old
// focus-the-register-filter behavior.
initCommandPalette();
// Pinned feedback mode (W3-I): the rail toggle (defaults OFF) captures one pin.
initFeedbackPin();
document.getElementById('railIdentity')?.addEventListener('click', (ev) => {
  ev.stopPropagation();
  identityMenu(WS, ev.currentTarget as HTMLElement);
});

/** Rail Clients-count badge + pinned clients (top 3 by attentionScore), from
 * the live roster. No badge / no pinned section renders until data arrives. */
function populateRail(rows: RegisterClient[]): void {
  const badge = document.getElementById('clientsBadge');
  if (badge) {
    badge.textContent = String(rows.length);
    badge.toggleAttribute('hidden', rows.length === 0);
  }
  const section = document.getElementById('pinnedSection');
  const host = document.getElementById('pinnedClients');
  if (!host || !section) return;
  if (!rows.length) { section.setAttribute('hidden', ''); return; }
  // FIX-audit F6: an ABSENT attentionScore is UNKNOWN, not "On track". num()
  // returns null for a missing/non-finite score; a null-scored client sorts last
  // and renders the NEUTRAL signal dot (base `.signal`, --sent-neutral) with an
  // honest title — never a fabricated calm/On-track state.
  const score = (r: RegisterClient): number | null => (typeof r.attentionScore === 'number' && Number.isFinite(r.attentionScore) ? r.attentionScore : null);
  const top = [...rows].sort((a, b) => (score(b) ?? -1) - (score(a) ?? -1)).slice(0, 3);
  section.removeAttribute('hidden');
  render(
    html`${top.map((r) => {
      const s = score(r);
      const cls = s === null ? '' : s > 0 ? 'attention' : 'calm';
      const title = s === null ? 'status not scored' : s > 0 ? 'Needs attention' : 'On track';
      return html`<a class="client-jump" href="#/client/${r.slug}">
        ${brandLogo(brandVisual(r))}
        <span>${r.brand}</span>
        <i class="signal ${cls}" title=${title}></i>
      </a>`;
    })}`,
    host,
  );
}

/** Rail-foot identity chip from the live AUTH.user (person initials avatar,
 * name, role). Person initials are correct here — this is a person, not a brand
 * logo (locked contract §5). Called after the auth gate confirms the session. */
function setRailIdentity(): void {
  const u = AUTH.user;
  const avatar = document.getElementById('railAvatar');
  const name = document.getElementById('railName');
  const role = document.getElementById('railRole');
  if (avatar) avatar.textContent = personInitials(u?.name);
  if (name) name.textContent = u?.name || 'Not signed in';
  if (role) role.textContent = u?.role ? u.role.charAt(0).toUpperCase() + u.role.slice(1) : '';
}

/* Hash router (CRM shell, Package 1). initRouter() installs the hashchange
 * listener (re-applies the route + scrolls to top on every in-app navigation);
 * boot() does the FIRST apply after auth. The transitional research/report/work
 * chapter sections still render into their existing mounts on a deep link. */
initRouter((route: Route) => {
  // Paint the client register when navigating INTO it (roster fetched fresh on
  // entry). boot() does the first paint when #/clients IS the arrival landing.
  if (route.area === 'clients') { void initRegister(); return; }
  // Client Cockpit (transitional): paint on entry / re-paint on tab change.
  // Loads its OWN per-client payload (cockpitData) — not the legacy demo run.
  if (route.area === 'cockpit') { void initCockpit(route); return; }
  // My Work (#/work) + Team (#/team) — the CRM cross-client screens (Package 5).
  // Neither touches the legacy run payload; both leave the cockpit torn down.
  if (route.area === 'mywork') { teardownCockpit(); void initWork(); return; }
  if (route.area === 'team') { teardownCockpit(); void initTeam(); return; }
  // Prospects (#/prospects) — a static honest surface (no fetch); paint it on
  // entry. Leaves the cockpit torn down like the other CRM screens.
  if (route.area === 'prospects') { teardownCockpit(); renderProspects(); return; }
  // Any non-cockpit area: return any relocated section body / the board to its
  // home so the deep-link research/report/work sections keep rendering.
  teardownCockpit();
  // LAZY legacy boot (locked contract: "no eager renderAll()"): the transitional
  // research/report/work sections need the legacy run payload — load it ONLY
  // when one of those areas is actually visited, never on #/clients. enterLegacy
  // consumes the route/deep-link on every navigation (payload loads once).
  if (LEGACY_AREAS.has(route.area)) void enterLegacy();
});

/* Compat-query normalization (review #3): a bare `/review/?task=42` or
 * `?load=…` with NO hash used to land on the default Clients route while the
 * board loaded hidden — a `?task=` drawer could then open over the wrong page.
 * Give those legacy query-only links an explicit legacy hash BEFORE the first
 * applyRoute (replaceState — no history entry, no hashchange): a task link lands
 * on the board (#s7, where openTaskDeepLink focuses it), a bare run link on the
 * report (#s0). An explicit hash always wins. */
(function normalizeCompatQuery(): void {
  if (location.hash) return;
  const params = new URLSearchParams(location.search);
  const target = params.has('task') ? '#s7' : params.has('load') ? '#s0' : null;
  if (target) history.replaceState(null, '', location.pathname + location.search + target);
})();

/* Early apply: collapse the shell to the arrival route's area IMMEDIATELY at
 * module scope — DOM-only (class toggles on the static sections), pre-auth-safe
 * (no fetch, no state read). A `#/portfolio` arrival redirects to #/clients here
 * (DOM-safe hash replace), so no special-case is needed. */
applyRoute();

/* Drag-and-drop is the sole remaining loader for a result JSON into the
 * transitional sections (the old "Load result JSON" chrome button is gone with
 * the rest of the old rail). No button needed — drop a file anywhere. */
['dragover', 'drop'].forEach((evName) => document.body.addEventListener(evName, (ev) => {
  ev.preventDefault();
  document.getElementById('shell')!.classList.toggle('droplite', evName === 'dragover');
  if (evName === 'drop') {
    document.getElementById('shell')!.classList.remove('droplite');
    const f = (ev as DragEvent).dataTransfer?.files[0];
    // FIX-audit F3: allocate the load token at the DROP (user-action start),
    // before reading the file — allocating it after f.text() resolved let a
    // slow read become the newest generation and win over a newer navigation.
    // The drop still supersedes any EARLIER in-flight auto-load (its token is
    // newer); only a load STARTED AFTER the drop wins, which is correct.
    if (f) { const token = beginLoad(); f.text().then((t) => { if (isLoadCurrent(token)) loadPayload(t, f.name); }); }
  }
}));
document.body.addEventListener('dragleave', () => document.getElementById('shell')!.classList.remove('droplite'));

/* The `/` shortcut opens the global command palette (W3-H, initCommandPalette
 * above) — the standard "slash to search" affordance, now a cross-surface
 * search over clients · your tasks · screens rather than focusing whichever
 * on-screen filter happened to be mounted. The palette's own handler ignores
 * "/" while the user is typing in a field, and is a no-op while it's open. */

/** Areas whose sections are fed by the transitional legacy run payload. */
const LEGACY_AREAS = new Set<Route['area']>(['work', 'research', 'report']);

/* LAZY legacy payload boot (locked contract: "no eager renderAll()"). The
 * transitional research/report/work sections render from a run payload (?load=
 * param, else the demo run). Loading it eagerly at startup booted the whole
 * legacy report machinery on a fresh #/clients load; deferring it to an actual
 * legacy visit fixed that.
 *
 * ensureLegacyPayload loads the payload AT MOST once but is NOT latched by a
 * boolean (review #2): a FAILED load must be retryable, so the shared promise
 * resets to null on rejection. It takes a load token (review #1) so a legacy
 * fetch that resolves AFTER the user has navigated into a cockpit never clobbers
 * the cockpit's run. It does NOT itself consume the route/deep-link — that is
 * enterLegacy's job, run once PER navigation so a second #notes/#s7 visit still
 * focuses its target even though the payload only loads once. */
let legacyLoad: Promise<void> | null = null;
function ensureLegacyPayload(): Promise<void> {
  if (!legacyLoad) {
    legacyLoad = (async () => {
      const params = new URLSearchParams(location.search);
      const src = params.get('load') || '/test-runs/elal-demo.json';
      const token = beginLoad();
      const res = await fetch(rawUrl(src));
      if (!res.ok) throw new Error(String(res.status));
      const text = await res.text();
      if (!isLoadCurrent(token)) return; // superseded by a newer load — discard
      loadPayload(text, src.split('/').pop());
    })().catch((e) => { legacyLoad = null; throw e; }); // reset so a failure can retry
  }
  return legacyLoad;
}

/** Render a legacy-load error into the ACTIVE route's section (review #2), not a
 * hidden #execBody the user can't see when the failure happened on #s7. */
function renderLegacyError(e: unknown): void {
  const secId = visibleSections(currentRoute())[0];
  const host = secId
    ? (document.querySelector<HTMLElement>(`#${secId} [id$="Body"]`) || document.getElementById(secId))
    : document.getElementById('execBody');
  if (host) {
    render(html`<div class="why">Could not load the run (${String((e as Error).message || e)}). Drop a result JSON anywhere on the page to retry.</div>`, host);
  }
}

/** Enter a legacy (transitional) area: ensure the payload is loaded, then — on
 * EVERY navigation — re-apply the route and consume any `?task=`/`#notes`
 * deep-link. Separating this from the once-only load (review #2) means a repeat
 * #notes visit still focuses the notes card. */
async function enterLegacy(): Promise<void> {
  try {
    await ensureLegacyPayload();
    applyRoute();
    // Re-render the legacy IMPACT/AIQ section into its OWN home mount (#serpBody
    // /#aiBody) on entry — this RESETS the shared render root (mountRoot.ts) back
    // to the legacy mount after a deep Search/AI page may have pointed it at the
    // cockpit page, so the legacy section's controls always act on their own DOM
    // (Package 4 — two mount points, no node relocation).
    const r = currentRoute();
    if (r.area === 'research' && r.sub === 'search') {
      const el = document.getElementById('serpBody');
      if (el) renderSearch(payloadStore.get(), el as HTMLElement);
    } else if (r.area === 'research' && r.sub === 'ai') {
      const el = document.getElementById('aiBody');
      if (el) renderAI(payloadStore.get(), el as HTMLElement);
    }
    openTaskDeepLink(WS);
  } catch (e) {
    renderLegacyError(e);
  }
}

/* boot (AUTH GATE, Phase 0): the first thing boot() does is `await AUTH.init()`
 * — nothing below (the roster GET /clients, the lazy legacy payload) fires until
 * a session is confirmed. `false` means the #authGate overlay is showing; boot()
 * returns rather than racing it with a fetch that would 401 again. `true` covers
 * both "signed in" and "service unreachable" (lib/auth.ts's doc comment), so the
 * offline fallbacks still run. */
async function boot(): Promise<void> {
  if (!(await AUTH.init())) return;
  // Persona landing (persona-foundation Task 3; routes updated for the shell
  // restructure, spec A5.4): a signed-in user with a held persona capacity
  // lands on that capacity's home ROUTE (am/bizdev → #/work · writer →
  // #/work?cat=content · management → #/portfolio — lib/personas.ts's
  // PERSONA_ROUTES seam, the ONE place these targets are named) instead of the
  // plain default. Two guards keep this a strict no-op for everyone else:
  // (1) resolveLanding() returns null for a user with no persona (or AUTH.user
  // null — offline/service-unreachable, where AUTH.init() still resolves
  // `true`), so the block is skipped and behavior is identical to before;
  // (2) an explicit deep-link is never overridden — shouldApplyLanding()
  // applies persona-landing ONLY on a bare arrival (no hash, no `?task=`). Any
  // hash (`#/…`, legacy `#sN`, `#notes`) or `?task=<id>` means the user asked
  // to be somewhere specific, and persona routing must not clobber that (the
  // router resolves each of those forms to the right area — lib/router.ts).
  // Both landing writes use location.replace(), never assign/href/hash-set
  // (CTO review 2026-07-12, C1): a pushed landing entry is a back-button trap —
  // Back lands on the bare self-re-redirecting arrival URL (and the cross-page
  // form re-fires the redirect every time), so the user can never Back out of
  // the app. replace() swaps the arrival entry for the landed one instead.
  if (shouldApplyLanding(location.hash, location.search) && AUTH.user) {
    const landing = resolveLanding(AUTH.user);
    if (landing) {
      if (landing.startsWith('/')) { location.replace(landing); return; } // cross-page
      location.replace(landing); // e.g. #/work — same-document hash replace; the router shows that area post-load
    }
  }
  // Rail-foot identity chip, now that the session is confirmed.
  setRailIdentity();
  // Notification bell (Task-Management Phase 1 / T1): mounted only AFTER the
  // auth gate confirms a session, so its /me/notifications poll never fires
  // pre-auth.
  const bellHost = document.getElementById('notifBell');
  if (bellHost) initNotifBell(bellHost);
  // The roster GET /clients — the ONE shared fetch. It paints the register (into
  // the hidden #clientsView when the arrival isn't #/clients) and, via
  // onClientsLoaded, populates the always-visible rail (Clients badge + pinned)
  // and the slug→brand map the cockpit breadcrumb reads. This is roster data,
  // NOT the legacy run payload — it triggers no /impact, /ai-models or section
  // rendering. A 401 is handled inside (the api client re-shows the gate).
  void initRegister();
  // My Work fetches /me/tasks once at boot too — the rail's My Work badge is a
  // live count of the user's open items, so it must populate even when the
  // arrival isn't #/work (it paints into the hidden #workView, exactly as the
  // register paints into a hidden #clientsView for the rail Clients badge).
  void initWork();

  const route = currentRoute();
  // Client Cockpit direct arrival (transitional): #/client/{slug} loads its OWN
  // client's latest run via cockpitData — never the legacy demo run.
  if (route.area === 'cockpit') { void initCockpit(route); return; }
  // Team direct arrival: paint the department rollup (My Work already fetched
  // above; its own view paints #workView whether or not it's the arrival area).
  if (route.area === 'team') { void initTeam(); return; }
  // Prospects direct arrival: paint the static honest surface.
  if (route.area === 'prospects') { renderProspects(); return; }
  // Legacy deep-link arrival (research/report/work, or a ?load=/?task= link):
  // NOW load the legacy payload and render those sections. Every other landing
  // (#/clients · #/prospects · #/team) skips it entirely — the register/
  // placeholder screens need nothing from the run payload.
  const params = new URLSearchParams(location.search);
  if (LEGACY_AREAS.has(route.area) || params.has('load') || params.has('task')) {
    void enterLegacy();
  }
}
boot();
